import React, { Suspense, lazy } from 'react';
import { ArrowLeft } from 'lucide-react';
import { FlowEditor } from '@/components/flow';
import type { FlowEditorHandle } from '@/components/flow';
import { ImageCropper } from '@/components/ImageCropper';
import { SmartSequenceDock } from '@/components/SmartSequenceDock';
import { SonicStudio } from '@/components/SonicStudio';
import { SettingsModal } from '@/components/SettingsModal';
import { SidebarDock } from '@/components/SidebarDock';
import { WelcomeScreen } from '@/components/WelcomeScreen';
// v1.4.106 画布顶栏右侧用户信息 + 积分（与首页顶部一致，复用 UserMenu）
import { UserMenu } from '@/components/home/layout/UserMenu';
import { UploadWorkModal } from '@/components/upload/UploadWorkModal';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { useModelStore } from '@/stores/modelStore';
import { useDataPersistence, getPersistedGroups } from '@/hooks/useDataPersistence';
import { useUndoHistory } from '@/hooks/useUndoHistory';
import { useClipboard } from '@/hooks/useClipboard';
import { useNodeOperations } from '@/hooks/useNodeOperations';
import { useGenerationOrchestrator } from '@/hooks/useGenerationOrchestrator';
import { usePanoramaGenerator } from '@/hooks/usePanoramaGenerator';
import { useMultiAngleGenerator } from '@/hooks/useMultiAngleGenerator';
import { useLightingGenerator } from '@/hooks/useLightingGenerator';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useTaskRecovery } from '@/hooks/useTaskRecovery';
import { NodeType, NodeStatus, SmartSequenceItem } from '@/types';
import { generateApi, GenerateRequest } from '@/services/generateApi';
import { toast } from 'sonner';

// SketchEditor / DirectorPanel / AssistantPanel / LayerEditPanel / ScriptPanel 体积大且按需使用，保留 lazy import
const SketchEditor = React.lazy(() => import('@/components/SketchEditor').then(m => ({ default: m.SketchEditor })));
const DirectorPanel = React.lazy(() => import('@/components/director/DirectorPanel').then(m => ({ default: m.DirectorPanel })));
const AssistantPanel = React.lazy(() => import('@/components/agent/AssistantPanel').then(m => ({ default: m.AssistantPanel })));
const LayerEditPanel = React.lazy(() => import('@/components/layerEdit/LayerEditPanel').then(m => ({ default: m.LayerEditPanel })));
const ScriptPanel = React.lazy(() => import('@/components/ScriptPanel').then(m => ({ default: m.ScriptPanel })));
// 智能剪辑面板（体积大且按需使用，同 LayerEditPanel lazy 范式）
const SmartEditPanel = React.lazy(() => import('@/components/smartEdit/SmartEditPanel').then(m => ({ default: m.SmartEditPanel })));
// §8-4 批量生视频编排（StepsBar「批量生视频」按钮：对已出图分镜批量创建 VIDEO_GENERATOR）
import { generateBatchVideos } from '@/components/ScriptPanel/useScriptBatchGenerator';
// v1.4.56 脚本资产生成同步（EditorLayout 全局挂载，面板关闭仍存活）：进度估算 + 终态写回 script.assets
import { useScriptAssetGenSync } from '@/components/ScriptPanel/useScriptAssets';
import { DirectorErrorBoundary } from '@/components/director/DirectorErrorBoundary';
// 智能剪辑面板错误边界（§4.10 数据保护）：面板渲染错误在此拦截，不冒泡到顶层炸掉整个应用
import { SmartEditErrorBoundary } from '@/components/smartEdit/SmartEditErrorBoundary';
import type { DirectorSceneData, DirectorNodeData, LayerDocument } from '@/types';
import type { EditDocument, SmartEditSession } from '@/types/smartEdit';

const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";
// 无限画布右下角智能体头像使用独立的 3D 表情动图
const AGENT_AVATAR_SRC = '/agent-3d.gif';

interface EditorLayoutProps {
  isTransitioning: boolean;
  transitionDirection: string | null;
  onBackToProjects: () => void;
  workflows: any[];
  selectedWorkflowId: string | null;
  onSelectWorkflow: (id: string) => void;
  onSaveWorkflow: () => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, title: string) => void;
  onOpenSettings: () => void;
  isSettingsOpen: boolean;
  onCloseSettings: () => void;
  isChatOpen: boolean;
  onToggleChat: () => void;
  isMultiFrameOpen: boolean;
  onToggleMultiFrame: () => void;
  isSonicStudioOpen: boolean;
  onToggleSonicStudio: () => void;
  assetHistory: any[];
  setAssetHistory: React.Dispatch<React.SetStateAction<any[]>>;
  sphereFlowing: boolean;
}

export const EditorLayout: React.FC<EditorLayoutProps> = ({
  isTransitioning,
  transitionDirection,
  onBackToProjects,
  isSettingsOpen,
  onCloseSettings,
  isChatOpen,
  onToggleChat,
  isMultiFrameOpen,
  onToggleMultiFrame,
  isSonicStudioOpen,
  onToggleSonicStudio,
  assetHistory,
  setAssetHistory,
  sphereFlowing,
}) => {
  const nodes = useNodeStore((s: any) => s.nodes);
  const loadModels = useModelStore((s: any) => s.loadModels);
  const loadFromLocalStorage = useTaskStore((s: any) => s.loadFromLocalStorage);
  const connectWebSocket = useTaskStore((s: any) => s.connectWebSocket);
  const disconnectWebSocket = useTaskStore((s: any) => s.disconnectWebSocket);

  const [croppingNodeId, setCroppingNodeId] = React.useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = React.useState<string | null>(null);
  const [isSketchEditorOpen, setIsSketchEditorOpen] = React.useState(false);
  const [isDirectorPanelOpen, setIsDirectorPanelOpen] = React.useState(false);
  const [directorNodeId, setDirectorNodeId] = React.useState<string | null>(null);
  // 图层编辑器状态（与导演台同构挂载；互斥见 onOpenLayerEdit）
  const [isLayerEditOpen, setIsLayerEditOpen] = React.useState(false);
  const [layerEditNodeId, setLayerEditNodeId] = React.useState<string | null>(null);
  // 脚本编辑面板状态（与导演台/图层编辑器互斥；见 onOpenScript）
  const [isScriptPanelOpen, setIsScriptPanelOpen] = React.useState(false);
  const [scriptNodeId, setScriptNodeId] = React.useState<string | null>(null);
  // 智能剪辑面板状态（四个全屏面板互斥；见 onOpenSmartEdit）
  const [isSmartEditOpen, setIsSmartEditOpen] = React.useState(false);
  const [smartEditNodeId, setSmartEditNodeId] = React.useState<string | null>(null);
  const [smartEditSession, setSmartEditSession] = React.useState<SmartEditSession | null>(null);
  // 上传作品弹窗（顶栏 UserMenu 上传按钮触发；仅画布有入口，首页不传）
  const [isUploadOpen, setIsUploadOpen] = React.useState(false);
  // 聊天面板首次打开后才加载对应 chunk；之后保持挂载以保留过渡动画与本地输入状态
  const [chatEverOpened, setChatEverOpened] = React.useState(false);
  React.useEffect(() => {
    if (isChatOpen) setChatEverOpened(true);
  }, [isChatOpen]);
  // 智能体面板 chunk 预热：新画布默认自动打开面板，若等 chatEverOpened 才触发
  // lazy 加载，首开瞬间叠加「下载大 chunk + 历史会话恢复请求」导致卡顿。
  // 编辑器挂载即预热模块（与 React.lazy 共享同一 chunk，二次 import 走缓存），
  // 不提前挂载组件。
  React.useEffect(() => {
    void import('@/components/agent/AssistantPanel');
  }, []);

  // 文档 4.2：useUndoHistory 替代 useHistory；useClipboard 内部管理 clipboard state
  const undoHistory = useUndoHistory();
  const { saveHistory, undo } = undoHistory;
  const clipboardApi = useClipboard({ saveHistory });
  const {
    handleAddNode, handleNodeUpdate, handleNodeUpdateWithHistory, handleNodeDelete,
    handleExtractImage, handleAssetGenerated,
  } = useNodeOperations(saveHistory, setAssetHistory);
  const { handleNodeAction } = useGenerationOrchestrator(handleNodeUpdate, handleAssetGenerated);
  const { handleGeneratePanorama } = usePanoramaGenerator(saveHistory, handleAssetGenerated);
  const { handleGenerateMultiAngle } = useMultiAngleGenerator(saveHistory, handleAssetGenerated);
  const { handleGenerateLighting } = useLightingGenerator(saveHistory, handleAssetGenerated);

  useTaskRecovery({ isLoaded: true, handleAssetGenerated });
  // v1.4.56 资产生成画布节点承载：全局同步扫描 scriptAssetRef 节点（进度估算/终态写回/孤儿清理）
  useScriptAssetGenSync();

  // 侧边栏「上传」命令式句柄：单击直接弹文件选择（复用 FlowEditor 内双击菜单「上传」同款链路）
  const flowEditorRef = React.useRef<FlowEditorHandle>(null);

  const onAddNode = React.useCallback((type: NodeType, position?: { x: number; y: number }, initialData?: any) => {
    const result = handleAddNode(type, position, initialData);
    if (result === 'open_sketch_editor') setIsSketchEditorOpen(true);
    return result;
  }, [handleAddNode]);

  // 侧边栏建节点包装：落点取当前画布可视区中心（画布未就绪时回退 handleAddNode 内的 (100,100) 兜底），
  // 创建成功后画面平滑聚焦到新节点（打灯式聚焦，落点已在视野中心时动画自动跳过）；画板哨兵返回值不聚焦
  const onSidebarAddNode = React.useCallback((type: NodeType, position?: { x: number; y: number }, initialData?: any) => {
    const center = flowEditorRef.current?.getViewportCenter() ?? undefined;
    const result = onAddNode(type, position ?? center, initialData);
    if (result && result !== 'open_sketch_editor') {
      flowEditorRef.current?.focusNode(result);
    }
    return result;
  }, [onAddNode]);

  const onOpenDirector = React.useCallback((nodeId: string) => {
    // 与图层编辑器互斥（4.1.3：两个全屏编辑器不同时叠放）
    setIsLayerEditOpen(false);
    setLayerEditNodeId(null);
    setDirectorNodeId(nodeId);
    setIsDirectorPanelOpen(true);
  }, []);

  const onOpenLayerEdit = React.useCallback((nodeId: string) => {
    // 与导演台互斥（4.1.3）
    setIsDirectorPanelOpen(false);
    setDirectorNodeId(null);
    setLayerEditNodeId(nodeId);
    setIsLayerEditOpen(true);
  }, []);

  const onOpenScript = React.useCallback((nodeId: string) => {
    // 三个全屏编辑器互斥：关闭导演台与图层编辑器
    setIsDirectorPanelOpen(false);
    setDirectorNodeId(null);
    setIsLayerEditOpen(false);
    setLayerEditNodeId(null);
    setScriptNodeId(nodeId);
    setIsScriptPanelOpen(true);
  }, []);

  const onOpenSmartEdit = React.useCallback((nodeId: string, session?: SmartEditSession | null) => {
    // 四面板互斥（§4.1.3）：关闭导演台/图层编辑器/脚本面板
    setIsDirectorPanelOpen(false);
    setDirectorNodeId(null);
    setIsLayerEditOpen(false);
    setLayerEditNodeId(null);
    setIsScriptPanelOpen(false);
    setScriptNodeId(null);
    setSmartEditSession(session ?? null);
    setSmartEditNodeId(nodeId);
    setIsSmartEditOpen(true);
  }, []);

  // 图层编辑器/脚本面板/智能剪辑面板打开期间停用全局快捷键（§6 #21：防编辑器内 Delete 误删主画布节点）
  useKeyboardShortcuts({ undoHistory, clipboard: clipboardApi, enabled: !isLayerEditOpen && !isScriptPanelOpen && !isSmartEditOpen });

  const handleSketchResult = (type: 'image' | 'video', result: string, prompt: string) => {
    handleAddNode(NodeType.IMAGE_GENERATOR, { x: 100, y: 100 }, { image: result, prompt, status: NodeStatus.SUCCESS });
    handleAssetGenerated(type, result, prompt || 'Sketch Output', prompt);
  };

  const handleMultiFrameGenerate = async (frames: SmartSequenceItem[]): Promise<string> => {
    const complexPrompt = "A sequence showing: " + frames.map(f => f.transition?.prompt || "scene").join(" transitioning to ");
    try {
      const request: GenerateRequest = {
        modelId: frames[0].src ? 'grsai-minimax-h3-img2video' : 'grsai-minimax-h3',
        prompt: complexPrompt,
        mode: frames[0].src ? 'image-to-video' : 'text-to-video',
        inputImages: frames[0].src ? [frames[0].src] : undefined,
        params: { aspectRatio: '16:9', referenceImages: frames.length > 1 ? frames.map(f => f.src) : undefined },
      };
      const res = await generateApi.generateSync(request);
      const uri = res.results?.[0]?.url;
      if (!uri) throw new Error('Smart Sequence Generation Failed');
      const isFallback = res.results?.[0]?.outputType === 'image';
      if (isFallback) handleAssetGenerated('image', uri, 'Smart Sequence Preview (Fallback)', complexPrompt);
      else handleAssetGenerated('video', uri, 'Smart Sequence', complexPrompt);
      return uri;
    } catch (e: any) {
      throw new Error(e.message || 'Smart Sequence Generation Failed');
    }
  };

  return (
    <div className={`w-full h-full overflow-hidden text-slate-200 selection:bg-cyan-500/30 transition-all duration-700 ease-[${SPRING}] ${isTransitioning && transitionDirection === 'toProjects' ? 'opacity-0 scale-105' : 'opacity-100 scale-100 animate-in fade-in slide-in-from-bottom-4'}`}>
      <div className="absolute inset-0 noise-bg" />
      {/* 流式布局：浮动 UI 原生尺寸定位（无缩放补偿壳） */}
      <div className="absolute top-6 left-6 z-[60]">
        <button onClick={onBackToProjects} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#363636] bg-[#0D0D0D] text-sm font-medium text-white shadow-lg transition-colors hover:border-[#4D4D4D] hover:bg-[#2D2D2D]">
          <ArrowLeft size={16} />
          <span>返回项目</span>
        </button>
      </div>
      {/* v1.4.107 顶栏右侧：用户信息 + 积分/会员胶囊（设计稿 217:1229，与首页顶部一致，复用 UserMenu）。
          v1.4.108 流式化：viewportScale 缩放补偿壳退役，wrapper 回归原生全屏层。
          保留全屏 wrapper 的原因不变——UserMenu 内部下拉的 fixed inset-0 关闭遮罩在有 transform
          的祖先内会以该祖先为包含块；wrapper 占满视口后遮罩恰好铺满全屏（无 transform 时包含块
          仍可能是 wrapper，保留全屏尺寸保证遮罩覆盖完整）。
          全屏 wrapper 会拦截画布交互，故外层 pointer-events none、内容容器显式恢复 auto。
          v1.4.107 智能体面板避让（设计稿 30:571）：面板打开时 UserMenu 整体右移到面板左侧、间距 16px
          （设计稿画布宽 1484 / 面板 x1484 / 用户胶囊右缘 1468）；面板宽度（可拖拽 380~800）由
          useResizablePanel 实时写入 --agent-panel-w（面板坐标系，与本 wrapper 同坐标系），拖拽期间
          html[data-agent-panel-resizing] 置位、CSS 里关闭 right 过渡保证跟手；开合时用与面板滑入
          相同的 500ms 弹簧过渡同步平移。 */}
      <div
        className="absolute inset-0 z-[60] pointer-events-none"
      >
        <div
          className="topbar-user-menu absolute top-6 pointer-events-auto transition-[right] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
          style={{ right: isChatOpen ? 'calc(var(--agent-panel-w, 420px) + 16px)' : '24px' }}
        >
          <UserMenu variant="canvas" onOpenUpload={() => setIsUploadOpen(true)} />
        </div>
      </div>

      <WelcomeScreen onAddNode={onAddNode} visible={nodes.length === 0} />

      <FlowEditor
        ref={flowEditorRef}
        onNodeAction={handleNodeAction}
        onNodeUpdate={handleNodeUpdateWithHistory}
        onNodeDelete={handleNodeDelete}
        onAddNode={onAddNode}
        onExtractImage={handleExtractImage}
        onGeneratePanorama={handleGeneratePanorama}
        onGenerateMultiAngle={handleGenerateMultiAngle}
        onGenerateLighting={handleGenerateLighting}
        onToggleChat={onToggleChat}
        onOpenChat={() => { if (!isChatOpen) onToggleChat(); }}
        onOpenDirector={onOpenDirector}
        onOpenLayerEdit={onOpenLayerEdit}
        onOpenScript={onOpenScript}
        onOpenSmartEdit={onOpenSmartEdit}
        saveHistory={saveHistory}
        pasteAt={clipboardApi.pasteAt}
        onUndo={undo}
        onPaste={clipboardApi.paste}
      />
      {croppingNodeId && imageToCrop && <ImageCropper imageSrc={imageToCrop} onCancel={() => { setCroppingNodeId(null); setImageToCrop(null); }} onConfirm={(b: any) => { handleNodeUpdateWithHistory(croppingNodeId, { croppedFrame: b }); setCroppingNodeId(null); setImageToCrop(null); }} />}
      {isSketchEditorOpen && <Suspense fallback={null}><SketchEditor onClose={() => setIsSketchEditorOpen(false)} onGenerate={handleSketchResult} /></Suspense>}
      {isDirectorPanelOpen && directorNodeId && (
        <DirectorErrorBoundary onClose={() => { setIsDirectorPanelOpen(false); setDirectorNodeId(null); }}>
          <Suspense fallback={null}>
            <DirectorPanel
              nodeId={directorNodeId}
              initialSceneData={(nodes.find((n: any) => n.id === directorNodeId)?.data as DirectorNodeData | undefined)?.sceneData}
              onSave={(sceneData: DirectorSceneData) => {
                handleNodeUpdateWithHistory(directorNodeId, { sceneData });
              }}
              onClose={() => {
                setIsDirectorPanelOpen(false);
                setDirectorNodeId(null);
              }}
            />
          </Suspense>
        </DirectorErrorBoundary>
      )}
      {isLayerEditOpen && layerEditNodeId && (
        <Suspense fallback={null}>
          <LayerEditPanel
            nodeId={layerEditNodeId}
            initialDocument={(nodes.find((n: any) => n.id === layerEditNodeId)?.data as any)?.layerDocument as LayerDocument | null | undefined}
            onSave={(layerDocument: LayerDocument, thumbnailDataUrl?: string, outputImageUrl?: string) => {
              // 手动保存：缩略图/输出资产与 layerDocument 同次写入（v0.29/v0.31/v0.33 时序），记主画布撤销历史
              handleNodeUpdateWithHistory(layerEditNodeId, {
                layerDocument,
                ...(thumbnailDataUrl ? { thumbnailDataUrl } : {}),
                ...(outputImageUrl ? { outputImageUrl } : {}),
                isEditorOpened: true,
              });
            }}
            onAutoSave={(layerDocument: LayerDocument) => {
              // 自动保存：静默更新（不入主画布撤销栈，跳过缩略图，方案 4.9）
              handleNodeUpdate(layerEditNodeId, { layerDocument });
            }}
            onClose={() => {
              setIsLayerEditOpen(false);
              setLayerEditNodeId(null);
            }}
          />
        </Suspense>
      )}
      {isSmartEditOpen && smartEditNodeId && (
        <SmartEditErrorBoundary onClose={() => {
          // 崩溃兜底关闭：同正常 onClose 清理会话状态（数据已由面板卸载保全写回节点）
          setIsSmartEditOpen(false);
          setSmartEditNodeId(null);
          setSmartEditSession(null);
        }}>
          <Suspense fallback={null}>
            <SmartEditPanel
              nodeId={smartEditNodeId}
              projectId={null}
              initialDocument={((nodes.find((n: any) => n.id === smartEditNodeId)?.data as any)?.editDocument as EditDocument | undefined) ?? null}
              session={smartEditSession}
              onSave={(editDocument: EditDocument, thumbnailDataUrl?: string) => {
                // 手动保存：封面与 editDocument 同次写入（对齐图层编辑 v0.29 时序），记主画布撤销历史
                handleNodeUpdateWithHistory(smartEditNodeId, {
                  editDocument,
                  ...(thumbnailDataUrl ? { thumbnailDataUrl } : {}),
                  isEditorOpened: true,
                });
              }}
              onAutoSave={(editDocument: EditDocument) => {
                // 自动保存：静默更新（不入主画布撤销栈，跳过封面，§4.10）
                handleNodeUpdate(smartEditNodeId, { editDocument });
              }}
              onClose={() => {
                setIsSmartEditOpen(false);
                setSmartEditNodeId(null);
                setSmartEditSession(null);
              }}
            />
          </Suspense>
        </SmartEditErrorBoundary>
      )}
      {isScriptPanelOpen && scriptNodeId && (
        <Suspense fallback={null}>
          <ScriptPanel
            nodeId={scriptNodeId}
            onClose={() => {
              setIsScriptPanelOpen(false);
              setScriptNodeId(null);
            }}
            onNodeAction={handleNodeAction}
            onAddNode={onAddNode}
            onBatchGenerateVideos={() => {
              if (!scriptNodeId) return;
              // §8-4：对已出图分镜批量创建 VIDEO_GENERATOR，复用生成编排串行队列
              generateBatchVideos({ scriptNodeId, onNodeAction: handleNodeAction, onAddNode });
            }}
          />
        </Suspense>
      )}
      <SmartSequenceDock isOpen={isMultiFrameOpen} onClose={() => onToggleMultiFrame()} onGenerate={handleMultiFrameGenerate} onConnectStart={(e: any) => { e.preventDefault(); e.stopPropagation(); }} />
      <SonicStudio isOpen={isSonicStudioOpen} onClose={() => onToggleSonicStudio()} history={assetHistory.filter((a: any) => a.type === 'audio')} onGenerate={(src: any, prompt: any) => handleAssetGenerated('audio', src, prompt, prompt)} />
      <SettingsModal isOpen={isSettingsOpen} onClose={onCloseSettings} />
      {/* 上传作品弹窗（设计稿 235:466/538/145 + 235:226 历史图库）；assetHistory 同步进历史图库数据源；
          workflowSnapshot 发布瞬间取当前画布全部节点/连线/分组（「查看制作流程」数据源） */}
      <UploadWorkModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        assetHistory={assetHistory}
        workflowSnapshot={() => ({
          nodes: useNodeStore.getState().nodes,
          connections: useNodeStore.getState().connections,
          groups: getPersistedGroups(),
        })}
      />
      {/* 画布左侧选择节点栏（设计稿 231:582） */}
      <SidebarDock onAddNode={onSidebarAddNode} onUploadClick={() => flowEditorRef.current?.openUploadPicker()} />
      {chatEverOpened && (
        /* 流式布局：智能体侧栏原生 fixed 全高右贴（无缩放补偿壳）；
           面板宽度由 useResizablePanel 运行时写入，wrapper 无布局子元素故宽度为 0，不会遮挡画布点击 */
        <div className="fixed top-0 right-0 z-40">
          <Suspense fallback={<div className="w-[420px] h-full bg-[#1c1c1e] animate-pulse" />}>
            <AssistantPanel isOpen={isChatOpen} onClose={() => onToggleChat()} />
          </Suspense>
        </div>
      )}

      {!isChatOpen && (
        <div className="fixed bottom-4 right-6 z-50">
          <div
            className="flex flex-col items-center gap-2 group"
            style={{ height: 133, transform: 'translate(0px, 5px)' }}
          >
            <span className="px-3 py-1.5 text-xs font-medium text-white/90 bg-[#1c1c1e]/90 backdrop-blur-xl border border-white/10 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
              Bong·ai Agent
            </span>
            <button
              onClick={() => onToggleChat()}
              className="flex items-center justify-center rounded-full hover:scale-110 transition-transform duration-300 cursor-pointer"
              title="打开智能体"
            >
              <img
                src={AGENT_AVATAR_SRC}
                alt="BongAI 助手"
                width={100}
                height={100}
                draggable={false}
                style={{ objectFit: 'contain', display: 'block' }}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
